import React, { FC, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Box, DialogContent, Modal, Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import cx from 'classnames';

import { translate } from '../../../../utils/helpers';
import { MY_ACCOUNT_LINKS } from '../../pages/const';
import { sortStateType, SORT_KEYS, USER_ROLES_NAMES } from './const';
import {
  GET_TEAM,
  GET_TEAMS_LIST,
  GET_TEAM_MEMBERS,
  UPDATE_MEMBER_ROLE,
  CHANGE_MEMBER_TEAM,
  REMOVE_LEARNER_FROM_ORG,
} from '../../../../graphql/user';
import {
  Query,
  USER_ROLES,
  TeamRole,
  Mutation,
  TeamMemberRole,
  GetTeamArgs,
  GetTeamMembersArgs,
  MoveLearnerInOtherTeamInput,
  LearnerInfo,
} from '../../../../types';
import { Loader } from '../../../../components/Loader';
import { CustomButton } from '../../../../components/UI/CustomButton';
import { sortByDate } from '../../../../utils/helpers';
import { CardSection } from '../CardSection';
import CardTitleWithNav from '../CardTitleWithNav';
import SingleTeamTableRow from '../SingleTeamTableRow';
import SingleTeamTableHeader from '../SingleTeamTableHeader';
import ChangeLearnerTeamModal from '../ChangeLearnerTeamModal';
import RemoveLearnerTeamModal from '../RemoveLearnerTeamModal';
import { useViewStyles } from '../OwnerAllTeamView';

const sortData = (data, key, state) => {
  if (key === SORT_KEYS.lastLogin) {
    return sortByDate(data, key, !state);
  }
  return data.sort((a, b) => {
    const aKey =
      key === SORT_KEYS.name ? a[key] : translate(`page.account.${USER_ROLES_NAMES[a[key]]}`);
    const bKey =
      key === SORT_KEYS.name ? b[key] : translate(`page.account.${USER_ROLES_NAMES[b[key]]}`);
    if (aKey > bKey) return state ? -1 : 1;
    if (bKey > aKey) return state ? 1 : -1;
    return null;
  });
};

interface SingleTeamViewProps {
  isOwner: boolean;
}

const SingleTeamView: FC<SingleTeamViewProps> = ({ isOwner }) => {
  const classes = useViewStyles();
  const params = useParams();
  const teamId = params?.id;

  const [sortState, setSortState] = useState<sortStateType>(null);
  const [sortedTeamMemders, setSortedTeamMemders] = useState<LearnerInfo[] | null>(null);
  const [selectedLearner, setSelectedLearner] = useState<LearnerInfo | null>(null);
  const [openChangeTeamModal, setOpenChangeTeamModal] = useState(false);
  const [openRemoveLearnerModal, setOpenRemoveLearnerModal] = useState(false);
  const [alert, setAlert] = useState('');

  const { data: membersList, loading, refetch: refetchMembersList } = useQuery<
    Pick<Query, 'getTeamMembers'>,
    GetTeamMembersArgs
  >(GET_TEAM_MEMBERS, { variables: { team_id: teamId } });

  const { data: teamData, loading: loadingTeamData } = useQuery<
    Pick<Query, 'getTeam'>,
    GetTeamArgs
  >(GET_TEAM, {
    variables: { id: teamId },
  });

  const { data: teamsData } = useQuery<Pick<Query, 'getTeamsList'>>(GET_TEAMS_LIST, {
    skip: !isOwner,
  });

  const currentTeam = teamData?.getTeam;
  const teamMembers = membersList?.getTeamMembers || [];
  const teams = teamsData?.getTeamsList || [];

  const [updateMemberRole] = useMutation<Pick<Mutation, 'updateMemberRole'>, TeamMemberRole>(
    UPDATE_MEMBER_ROLE,
    {
      onCompleted: () => {
        const role = selectedLearner?.role === USER_ROLES?.TeamManager ? 'Learner' : 'Manager';
        refetchMembersList();
        setAlert(translate('alert.teamRoleUpdated', { role }));
        setSelectedLearner(null);
      },
    },
  );

  const [moveLearnerToOtherTeam] = useMutation<
    Pick<Mutation, 'moveLearnerToOtherTeam'>,
    MoveLearnerInOtherTeamInput
  >(CHANGE_MEMBER_TEAM, {
    onCompleted: () => {
      refetchMembersList();
      setOpenChangeTeamModal(false);
      setAlert(translate('alert.learnerChangedTeam'));
    },
  });

  const [removeLearnerFromCompany] = useMutation<Pick<Mutation, 'removeLearnerFromCompany'>>(
    REMOVE_LEARNER_FROM_ORG,
    {
      onCompleted: () => {
        refetchMembersList();
        setSelectedLearner(null);
        setOpenRemoveLearnerModal(false);
        setAlert(translate('alert.learnerRemovedFromOrg'));
      },
      variables: { learner_id: selectedLearner?.id },
    },
  );

  const hideAlert = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlert('');
  };

  const onChangeMemberRole = (member: LearnerInfo) => {
    const newRole = member.role === USER_ROLES.TeamManager ? TeamRole.Member : TeamRole.Manager;
    setSelectedLearner(member);
    updateMemberRole({
      variables: {
        input: {
          team_id: teamId,
          member_id: member?.id,
          member_role: newRole,
        },
      },
    });
  };

  const onChangeTeam = (member: LearnerInfo) => {
    setSelectedLearner(member);
    setOpenChangeTeamModal(true);
  };

  const onRemoveUser = (member: LearnerInfo) => {
    setSelectedLearner(member);
    setOpenRemoveLearnerModal(true);
  };

  const handleSort = (key) => {
    let state = sortState?.key === key ? 0 : 1;
    if (sortState?.key === key && sortState?.state === 0) {
      setSortState(null);
      setSortedTeamMemders(null);
      return;
    }
    const membersToSort = [...teamMembers];
    const sortedTeamsArr = sortData(membersToSort, key, state);
    setSortState({ key, state });
    setSortedTeamMemders(sortedTeamsArr);
  };

  const teamToDisplay = sortState ? sortedTeamMemders : teamMembers;
  const goBackLink = isOwner ? MY_ACCOUNT_LINKS.MyTeams : null;

  if (loading || loadingTeamData) return <Loader />;

  return (
    <>
      <Box>
        <CardTitleWithNav
          goBackLink={goBackLink}
          titleClass={classes.titleWrap}
          wrapperClass={classes.headerWrapper}
          iconClass="header-icon"
          title={translate('page.account.teamName', { name: currentTeam?.name })}
          subTitle={translate('page.account.learnersAmount', { amount: teamMembers?.length })}
        >
          <Link
            className={classes.headerButton}
            to={`${MY_ACCOUNT_LINKS.MyTeams}/${teamId}/invite`}
          >
            <CustomButton>{translate('page.account.inviteLearners')}</CustomButton>
          </Link>
        </CardTitleWithNav>
        <CardSection
          title={translate('page.account.individualTeamDetails')}
          text={[translate('page.account.individualTeamManage')]}
          classes={{
            content: classes.container,
            textWrapper: classes.textWrap,
            title: classes.cardTitle,
          }}
        >
          <Box className={classes.tableWrap}>
            <Box className={classes.table}>
              <SingleTeamTableHeader handleSort={handleSort} sortState={sortState} />
              {teamToDisplay?.map((member: LearnerInfo) => (
                <SingleTeamTableRow
                  isOwner={isOwner}
                  member={member}
                  onChangeTeam={() => onChangeTeam(member)}
                  onChangeRole={() => onChangeMemberRole(member)}
                  onRemoveUser={() => onRemoveUser(member)}
                />
              ))}
            </Box>
          </Box>
          <Link to={`${MY_ACCOUNT_LINKS.MyTeams}/${teamId}/invite`}>
            <CustomButton btnClass={cx(classes.button, classes.buttonMobile)}>
              {translate('page.account.inviteLearners')}
            </CustomButton>
          </Link>
        </CardSection>
      </Box>

      {openChangeTeamModal && (
        <Modal open={openChangeTeamModal} className={classes.modal}>
          <DialogContent className={classes.dialog}>
            <ChangeLearnerTeamModal
              currentTeam={currentTeam}
              learner={selectedLearner}
              teams={teams}
              onChangeTeam={moveLearnerToOtherTeam}
              setLeaveModal={() => setOpenChangeTeamModal(false)}
            />
          </DialogContent>
        </Modal>
      )}
      {openRemoveLearnerModal && (
        <Modal open={openRemoveLearnerModal} className={classes.modal}>
          <DialogContent className={classes.dialog}>
            <RemoveLearnerTeamModal
              learner={selectedLearner?.name || ''}
              onClick={removeLearnerFromCompany}
              setLeaveModal={() => setOpenRemoveLearnerModal(false)}
            />
          </DialogContent>
        </Modal>
      )}

      <Snackbar open={!!alert} autoHideDuration={3000} onClose={hideAlert}>
        <MuiAlert elevation={6} variant="filled" onClose={hideAlert} severity="success">
          {alert}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default SingleTeamView;
